// Pantalla 14 · Configuración global (Super Admin), port del mock portal-super-admin-configuracion:
// parámetros, feature flags y auditoría de loan-config. Guardar pide un motivo por lote.
import { useMemo, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '@/kit';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { formatDateMx } from '@/lib/dateHelpers';
import { PageIntro } from '@/shared/components/PageIntro';
import { SectionCard } from '@/shared/components/SectionCard';
import { MockBoundary } from '@/shared/components/MockBoundary';
import { KpiBar, type KpiItem } from '@/shared/patterns/KpiBar';
import { SaveBar } from '@/shared/patterns/SaveBar';
import { AuditTable } from './AuditTable';
import { ParamRow } from './ParamRow';
import { useRecentParameterChanges } from './api/useRecentParameterChanges';
import { useSaveSystemParams, useSystemParams } from './api/useSystemParams';
import { isBackedParam, PARAM_BINDINGS } from './mapping';
import { resolveGlobalConfigAbilities } from './permissions';
import { formatParamValue, lastModificationAt, toAuditRow } from './recentChanges';
import { isValidSaveReason, SAVE_REASON_MIN_LENGTH } from './saveReason';
import { PARAM_UNIT, type ParamCategory, type ParamValue, type SystemParam } from './types';

const SECTIONS: { category: ParamCategory; title: string; meta: string; footnote?: ReactNode }[] = [
    {
        category: 'wizard',
        title: '1 · Wizard · ventanas de tiempo',
        meta: 'Vigencias que rigen el flujo POS del vendedor',
    },
    {
        category: 'engine',
        title: '2 · Motor de crédito · umbrales',
        meta: 'Score mínimo y pesos de capas del motor',
        footnote: (
            <>
                <strong>Referencia:</strong> los rangos SCI por nivel (Bronce 21-60, Plata 61-80,
                Oro 81+) y las TNA/enganche por nivel son parámetros comerciales — se administran en
                Políticas del Admin SK, no aquí.
            </>
        ),
    },
    {
        category: 'collections',
        title: '3 · Cobranza · SLAs',
        meta: 'Tiempos máximos de atención por severidad y umbrales del ciclo de mora',
    },
    {
        category: 'fraud',
        title: '4 · Fraude · multiplicadores default',
        meta: 'Umbrales de los detectores C1-C4 y multiplicador anti-fraude',
        footnote: (
            <>
                <strong>Nota:</strong> el multiplicador anti-fraude default aplica a todas las
                tiendas nuevas. El Admin VL puede sobreescribirlo por tienda desde Conciliación
                diaria, sin cambiar el default aquí.
            </>
        ),
    },
    {
        category: 'flags',
        title: '5 · Feature flags',
        meta: 'Interruptores de módulos y features · desactivar puede afectar rollout activo',
    },
    {
        category: 'contract',
        title: '6 · Contrato de crédito',
        meta: 'Datos que imprime el contrato · aplica a los contratos que se generen después',
    },
];

const SAVE_REASON_FIELD_ID = 'global-config-save-reason';

function sameValue(a: ParamValue, b: ParamValue): boolean {
    if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) < 1e-6;
    return a === b;
}

export function GlobalConfigPage() {
    const { permissions } = useAuth();
    const abilities = useMemo(() => resolveGlobalConfigAbilities(permissions), [permissions]);
    const { params, lastUpdatedAt, isMock } = useSystemParams(abilities.canRead);
    const recent = useRecentParameterChanges(abilities.canViewAudit);
    const save = useSaveSystemParams();
    const [edits, setEdits] = useState<ReadonlyMap<string, ParamValue>>(new Map());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [saveWarning, setSaveWarning] = useState<string | null>(null);

    const byCategory = useMemo(() => {
        const map = new Map<ParamCategory, SystemParam[]>();
        for (const p of params) {
            map.set(p.category, [...(map.get(p.category) ?? []), p]);
        }
        return map;
    }, [params]);

    const auditRows = useMemo(
        () => (recent.data?.items ?? []).map((change) => toAuditRow(change, params)),
        [recent.data, params],
    );

    if (!abilities.canRead) {
        return (
            <div role="alert" className="p-6 text-[12.5px] text-muted-foreground">
                No tienes permiso para ver la configuración global. Si crees que es un error,
                contacta al administrador.
            </div>
        );
    }

    const readOnly = !abilities.canUpdate || isMock;
    const pendingCount = edits.size;
    const reasonReady = isValidSaveReason(reason);
    // Un texto vacío borraría el dato del contrato: no se guarda.
    const hasBlankText = [...edits.values()].some(
        (value) => typeof value === 'string' && value.trim() === '',
    );

    const setParam = (id: string, value: ParamValue): void => {
        const param = params.find((p) => p.id === id);
        if (!param) return;
        setEdits((prev) => {
            const next = new Map(prev);
            if (sameValue(value, param.value)) next.delete(id);
            else next.set(id, value);
            return next;
        });
    };

    const changeDialog = (open: boolean): void => {
        setConfirmOpen(open);
        if (!open) setReason('');
    };

    const applyChanges = (): void => {
        if (!reasonReady || hasBlankText) return;
        const savedReason = reason.trim();
        void (async () => {
            const backed = [...edits]
                .filter(([id]) => isBackedParam(id))
                .map(([id, value]) => ({ id, value }));
            // Un valor inválido no tumba el lote: vuelve en `failed` y esa edición se conserva.
            const failedKeys = new Set<string>();
            try {
                if (backed.length > 0) {
                    const r = await save.mutateAsync({ edits: backed, reason: savedReason });
                    for (const x of r.failed) failedKeys.add(`${x.paramType}#${x.key}`);
                }
            } catch {
                return;
            }
            setEdits(
                (prev) =>
                    new Map(
                        [...prev].filter(([id]) => {
                            const b = PARAM_BINDINGS[id];
                            return b ? failedKeys.has(`${b.paramType}#${b.key}`) : false;
                        }),
                    ),
            );
            if (failedKeys.size > 0) {
                setSaveWarning(
                    `El servidor rechazó ${String(failedKeys.size)} valor(es) por tipo inválido: ${[...failedKeys].join(', ')}`,
                );
                return;
            }
            setSaveWarning(null);
            changeDialog(false);
        })();
    };

    // ---------- KPIs ----------
    const connectedFlags = params.filter((p) => p.category === 'flags' && isBackedParam(p.id));
    const flagsOn = connectedFlags.filter((p) => p.value === true).length;
    const outOfDefault = params.filter(
        (p) => p.category !== 'flags' && !sameValue(p.value, p.defaultValue),
    ).length;
    const editables = params.filter((p) => p.editable && isBackedParam(p.id)).length;
    const lastAt = lastModificationAt(recent.data?.items ?? [], lastUpdatedAt);
    const kpis: KpiItem[] = [
        {
            label: 'Parámetros',
            value: params.length,
            sub: `${String(editables)} editables`,
        },
        {
            label: 'Feature flags activos',
            value: flagsOn,
            tone: 'success',
            sub: `${String(flagsOn)} de ${String(connectedFlags.length)} flags conectados`,
        },
        {
            label: 'Fuera del default',
            value: outOfDefault,
            tone: 'warning',
            sub: 'parámetros con valor personalizado',
        },
        {
            label: 'Cambios 30d',
            value: abilities.canViewAudit ? (recent.data?.totalInWindow ?? '—') : '—',
            sub: abilities.canViewAudit ? 'registrados en auditoría' : 'sin permiso de auditoría',
        },
        {
            label: 'Última modificación',
            value: lastAt ? formatDateMx(lastAt) : '—',
            sub: auditRows[0]?.name ?? 'según los parámetros guardados',
        },
    ];

    const renderRows = (category: ParamCategory): ReactNode =>
        (byCategory.get(category) ?? []).map((param) => (
            <ParamRow
                key={param.id}
                param={param}
                value={edits.get(param.id) ?? param.value}
                dirty={edits.has(param.id)}
                readOnly={readOnly}
                notConnected={!isBackedParam(param.id)}
                onChange={setParam}
            />
        ));

    return (
        // Full-bleed dentro del content del AppShell: intro y KPI bar van de borde a borde y el
        // SaveBar queda sticky al fondo. El MockBoundary marca la pantalla si el servidor no respondió.
        <MockBoundary active={isMock} className="-mx-5 -my-4 flex min-h-[calc(100%+2rem)] flex-col">
            <PageIntro>
                ¿Qué parámetros del sistema están vigentes hoy? Cambios afectan a toda la operación
                futura; los datos ya generados conservan las condiciones del momento. Los parámetros
                comerciales (planes, promos, recarga, bono PCF) viven en Políticas del Admin SK. Los
                overrides operativos por tienda viven en Conciliación del Admin VL.
            </PageIntro>

            <KpiBar items={kpis} />

            <div className="flex-1 space-y-2.5 p-2.5">
                {SECTIONS.map((section) => (
                    <SectionCard
                        key={section.category}
                        title={section.title}
                        meta={section.meta}
                        footnote={section.footnote}
                    >
                        <div className="px-2.5 py-1.5">{renderRows(section.category)}</div>
                    </SectionCard>
                ))}

                <SectionCard
                    title="7 · Auditoría · cambios recientes"
                    meta="Historial de modificaciones con valor antes y después"
                >
                    {abilities.canViewAudit ? (
                        <AuditTable
                            rows={auditRows}
                            loading={recent.isLoading}
                            failed={recent.isError}
                        />
                    ) : (
                        <p className="px-3 py-3 text-[11px] text-muted-foreground">
                            No tienes permiso para ver la auditoría de parámetros. Si crees que es
                            un error, contacta al administrador.
                        </p>
                    )}
                </SectionCard>
            </div>

            {readOnly ? null : (
                <SaveBar
                    count={pendingCount}
                    message={
                        pendingCount === 1
                            ? '1 parámetro modificado sin guardar'
                            : `${String(pendingCount)} parámetros modificados sin guardar`
                    }
                    onDiscard={() => {
                        setEdits(new Map());
                        setSaveWarning(null);
                    }}
                    onSave={() => {
                        setConfirmOpen(true);
                    }}
                />
            )}

            <ConfirmDialog
                open={confirmOpen}
                onOpenChange={changeDialog}
                title="Guardar cambios de configuración"
                confirmLabel="Guardar"
                loading={save.isPending}
                confirmDisabled={!reasonReady || hasBlankText}
                onConfirm={applyChanges}
                description={
                    <span className="block space-y-2 text-left">
                        <span className="block">
                            Se aplican{' '}
                            {pendingCount === 1 ? '1 cambio' : `${String(pendingCount)} cambios`}{' '}
                            con registro de auditoría:
                        </span>
                        <span className="block font-mono text-xs">
                            {[...edits.entries()].map(([id, next]) => {
                                const param = params.find((p) => p.id === id);
                                if (!param) return null;
                                const unit = PARAM_UNIT[param.kind];
                                return (
                                    <span key={id} className="block">
                                        {param.name}: {formatParamValue(param.value)} →{' '}
                                        <strong>
                                            {formatParamValue(next)}
                                            {unit ? ` ${unit}` : ''}
                                        </strong>
                                    </span>
                                );
                            })}
                        </span>
                    </span>
                }
            >
                <div className="space-y-1">
                    <label htmlFor={SAVE_REASON_FIELD_ID} className="text-[11px] font-semibold">
                        Motivo del cambio
                    </label>
                    <Textarea
                        id={SAVE_REASON_FIELD_ID}
                        className="min-h-[64px] text-[12px]"
                        value={reason}
                        aria-describedby={`${SAVE_REASON_FIELD_ID}-hint`}
                        onChange={(event) => {
                            setReason(event.target.value);
                        }}
                    />
                    <p
                        id={`${SAVE_REASON_FIELD_ID}-hint`}
                        className="text-[10.5px] text-muted-foreground"
                    >
                        {reasonReady
                            ? 'Queda en la auditoría de cada cambio del lote.'
                            : `Escribe al menos ${String(SAVE_REASON_MIN_LENGTH)} caracteres.`}
                    </p>
                    {save.isError ? (
                        <p className="text-[11px] font-semibold text-destructive">
                            No se pudo guardar: {save.error.message}
                        </p>
                    ) : null}
                    {saveWarning ? (
                        <p className="text-[11px] font-semibold text-warning">{saveWarning}</p>
                    ) : null}
                    {hasBlankText ? (
                        <p className="text-[11px] font-semibold text-destructive">
                            Hay un texto vacío: complétalo antes de guardar.
                        </p>
                    ) : null}
                </div>
            </ConfirmDialog>
        </MockBoundary>
    );
}
